// client/src/components/collab/InviteCollaboratorModal.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { sessionRequestConfig } from "../../services/sessionLocal";

/** Try a list of request functions until one succeeds. */
const fetchFirst = async (attempts) => {
  let lastErr;
  for (const attempt of attempts) {
    try { return await attempt(); } catch (e) { lastErr = e; }
  }
  throw lastErr || new Error("All requests failed");
};

export default function InviteCollaboratorModal({
  tenantIdForRoutes,
  eventId,
  open,
  onClose,
  onInvited,
}) {
  const [enabled, setEnabled] = useState(false);
  const [search, setSearch] = useState("");
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [email, setEmail] = useState(""); // optional email to accompany invite
  const [role, setRole] = useState("viewer");
  const [loading, setLoading] = useState(false);

  const cfg = sessionRequestConfig();

  const loadTenants = async (q = "") => {
    setLoading(true);
    try {
      const res = await fetchFirst([
        () => axios.get(`/api/collab/search-tenants?q=${encodeURIComponent(q)}`, cfg),
        () => axios.get(`/api/tenants/search?q=${encodeURIComponent(q)}`, cfg),
        () => axios.get(`/api/tenants`, cfg),
      ]);
      const list = res?.data?.tenants || res?.data?.data || res?.data || [];
      const normalized = list
        .map((t) => ({
          id: Number(t.id || t.tenantid || t.tenantId),
          name: String(t.name || t.tenantname || t.email || `Tenant ${t.id}`),
        }))
        .filter((t) => t.id);
      setTenants(normalized);
    } catch {
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && enabled) loadTenants("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, enabled]);

  const submit = async (e) => {
    e.preventDefault();
    if (!enabled) return onClose?.();
    if (!selectedTenantId) return;

    try {
      await fetchFirst([
        () =>
          axios.post(
            `/api/collab/events/${eventId}/invite`,
            {
              sharedWithTenantId: Number(selectedTenantId),
              role,
              inviteeEmail: email || undefined,
            },
            cfg
          ),
        () =>
          axios.post(
            `/api/collab/${tenantIdForRoutes}/events/${eventId}/share`,
            {
              sharedWithTenantId: Number(selectedTenantId),
              role,
              inviteeEmail: email || undefined,
            },
            cfg
          ),
      ]);
      onInvited?.();
      onClose?.();
    } catch (err) {
      console.error("Invite failed:", err);
      onClose?.();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <form onSubmit={submit} className="bg-white dark:bg-neutral-900 p-4 rounded-xl w-[480px] shadow-xl">
        <h3 className="text-lg font-semibold mb-3">Collaboration</h3>

        <label className="flex items-center gap-2 mb-3 select-none">
          <input
            type="checkbox"
            role="switch"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <span className="text-sm">Enable collaboration for this event</span>
        </label>

        <div className={enabled ? "opacity-100" : "opacity-50 pointer-events-none"}>
          <label className="block text-sm mb-1">Tenant</label>
          <div className="flex gap-2 mb-2">
            <input
              className="w-full p-2 rounded border"
              placeholder="Search tenants by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); loadTenants(search); }
              }}
            />
            <button
              type="button"
              className="px-3 py-2 rounded border"
              onClick={() => loadTenants(search)}
            >
              {loading ? "…" : "Search"}
            </button>
          </div>

          <select
            className="w-full p-2 rounded border mb-3"
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
          >
            <option value="">Select a tenant…</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} (#{t.id})
              </option>
            ))}
          </select>

          <label className="block text-sm mb-1">Invitee email (optional)</label>
          <input
            className="w-full mb-3 p-2 rounded border"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="someone@company.com"
          />

          <label className="block text-sm mb-1">Role</label>
          <select
            className="w-full mb-4 p-2 rounded border"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            <option value="viewer">Viewer — read-only access</option>
            <option value="contributor">Contributor — can post, cannot edit event</option>
            <option value="editor">Editor — full edit access</option>
          </select>
        </div>

        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded border">
            Close
          </button>
          <button
            type="submit"
            className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
          >
            {enabled ? "Send invite" : "Done"}
          </button>
        </div>
      </form>
    </div>
  );
}
